import { useEffect, useState } from 'react';
import { useCoordinatesCache } from 'hooks/useCoordinatesCache';
import { geocodeAddress } from 'utils/geocoding';
import type { DealCoordinates } from '../dealsMapGeoJson';
import { Deal } from '../types';

const buildSearchQuery = (deal: Deal): string | null => {
    const parts = [deal.address, deal.city, deal.state, deal.zipCode].filter(Boolean);
    return parts.length > 0 ? parts.join(', ').trim() : null;
};

/**
 * Takes an array of deals and returns a map of dealId -> { lat, lng }
 * for deals that lack stored coordinates but have an address.
 * Uses the coordinates cache and geocodes missing ones in batch.
 */
export const useBatchGeocodedDeals = (deals: Deal[]) => {
    const [geocodedMap, setGeocodedMap] = useState<Record<number, DealCoordinates>>({});
    const { getCachedCoordinates, setCachedCoordinates } = useCoordinatesCache();

    useEffect(() => {
        const dealsNeedingGeocoding = deals.filter(
            deal => (deal.latitude == null || deal.longitude == null) && buildSearchQuery(deal) != null,
        );

        if (dealsNeedingGeocoding.length === 0) return;

        // Check cache first, collect uncached
        const fromCache: Record<number, DealCoordinates> = {};
        const uncached: { deal: Deal; query: string }[] = [];

        dealsNeedingGeocoding.forEach(deal => {
            const query = buildSearchQuery(deal)!;
            const cached = getCachedCoordinates(query);
            if (cached) {
                fromCache[deal.id] = { lat: cached.lat, lng: cached.lng };
            } else {
                uncached.push({ deal, query });
            }
        });

        if (Object.keys(fromCache).length > 0) {
            setGeocodedMap(prev => ({ ...prev, ...fromCache }));
        }

        if (uncached.length === 0) return;

        const abortController = new AbortController();
        const { signal } = abortController;

        const geocodeBatch = async () => {
            const results: Record<number, DealCoordinates> = {};

            // Geocode sequentially to avoid rate limiting
            for (const { deal, query } of uncached) {
                if (signal.aborted) return;
                const result = await geocodeAddress(query);
                if (signal.aborted) return;
                if (result && result.lat != null && result.lng != null) {
                    setCachedCoordinates(query, result);
                    results[deal.id] = { lat: result.lat, lng: result.lng };
                }
            }

            if (signal.aborted) return;
            if (Object.keys(results).length > 0) {
                setGeocodedMap(prev => ({ ...prev, ...results }));
            }
        };

        void geocodeBatch();

        return () => {
            abortController.abort();
        };
    }, [deals, getCachedCoordinates, setCachedCoordinates]);

    return geocodedMap;
};
